import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeftRight, Maximize2 } from "lucide-react";

// Global ergonomic settings for the Blog Editor workspace (NOT the rendered
// blog styling — that lives in the "Global Section Editor" tab). All admins
// share this config so the editor's layout stays consistent across sessions.
export default function BlogEditorUISettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { api.get("/site-settings/blog-editor-ui").then(r => setSettings(r.data)); }, []);

  const set = (patch) => { setSettings(prev => ({ ...prev, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/site-settings/blog-editor-ui", settings);
      setSettings(r.data);
      setDirty(false);
      toast.success("Editor settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!settings) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div data-testid="blog-editor-ui-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-heading text-xl font-semibold text-slate-900">Blog Visual Editor settings</div>
          <div className="text-sm text-slate-500">How the Blog Editor workspace behaves — layout, focus, and defaults.</div>
        </div>
        <Button data-testid="editor-ui-save" onClick={save} disabled={saving || !dirty} className="bg-sky-700 hover:bg-sky-600">
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </Button>
      </div>

      <div className="space-y-3">
        {/* Flip panes */}
        <div className="border border-slate-200 rounded-lg p-4 flex items-start gap-4 bg-white">
          <ArrowLeftRight className="w-5 h-5 mt-0.5 text-sky-700 shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-slate-900">Flip left / right panes</div>
            <div className="text-sm text-slate-500 mt-0.5">
              Put the controls (metadata + TOC + inline editor) on the RIGHT and the live preview on the LEFT.
              Handy for right-handed reading flow, or if your dominant monitor is on the left.
            </div>
          </div>
          <Switch data-testid="editor-ui-flip"
                  checked={settings.flip_panes}
                  onCheckedChange={v => set({ flip_panes: v })}/>
        </div>

        {/* Editor full pane */}
        <div className="border border-slate-200 rounded-lg p-4 flex items-start gap-4 bg-white">
          <Maximize2 className="w-5 h-5 mt-0.5 text-sky-700 shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-slate-900">Editor takes full pane when editing</div>
            <div className="text-sm text-slate-500 mt-0.5">
              When you open a section for editing, hide the metadata + TOC block above so the inline editor fills the entire
              controls pane. Great for focused writing. (Access metadata again by closing the inline editor with the X.)
            </div>
          </div>
          <Switch data-testid="editor-ui-full-pane"
                  checked={settings.editor_full_pane}
                  onCheckedChange={v => set({ editor_full_pane: v })}/>
        </div>

        {/* Preview ratio */}
        <div className="border border-slate-200 rounded-lg p-4 flex items-start gap-4 bg-white">
          <div className="w-5 h-5 mt-0.5 text-sky-700 shrink-0 font-heading text-[13px] font-bold">%</div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-slate-900">Preview pane width</div>
            <div className="text-sm text-slate-500 mt-0.5">
              How much of the horizontal space the live preview takes. Balance between more room for typing (lower)
              and more room to see the finished look (higher).
            </div>
            <div className="mt-3 flex items-center gap-3 max-w-md">
              <input type="range" min="30" max="70" step="5"
                     value={settings.preview_ratio}
                     onChange={e => set({ preview_ratio: Number(e.target.value) })}
                     className="flex-1"
                     data-testid="editor-ui-preview-ratio"/>
              <Input type="number" min="30" max="70" value={settings.preview_ratio}
                     onChange={e => set({ preview_ratio: Math.max(30, Math.min(70, Number(e.target.value) || 50)) })}
                     className="w-20 h-8 text-sm"/>
              <span className="text-xs text-slate-400 w-4">%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
